import React, { useEffect, useState } from "react";
import { USE_MOCK_DATA, useAppContext, RoleType } from "../../lib/mockState";
import { useAuth } from "../../contexts/AuthContext";
import { 
  Building2, 
  Calendar, 
  Flag, 
  Users, 
  Scale, 
  FileText, 
  PenTool, 
  Bot, 
  BookOpen, 
  PlusCircle, 
  CheckSquare, 
  Layers, 
  Clock, 
  CreditCard, 
  BarChart3, 
  Search, 
  Mail, 
  Files, 
  Settings,
  Languages,
  UserCheck,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useTheme } from "../../contexts/ThemeContext";
import { RESPONSIVE_BREAKPOINTS, useMediaQuery } from "./responsive";

interface ShellProps {
  children: React.ReactNode;
}

interface NavSectionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  isAr: boolean;
  sidebarOpen: boolean;
  children: React.ReactNode;
}

const NavSection: React.FC<NavSectionProps> = ({ title, isOpen, onToggle, isAr, sidebarOpen, children }) => {
  if (!sidebarOpen) return <div className="space-y-1.5">{children}</div>;
  
  return (
    <div className="mb-1">
      <button 
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-3 py-2 text-[9px] font-mono font-bold tracking-widest text-slate-400 hover:text-slate-600 uppercase group transition-colors ${isAr ? "flex-row-reverse" : ""}`}
      >
        <span className={isAr ? "text-right" : ""}>{title}</span>
        {isOpen ? <ChevronDown size={10} className="opacity-50 group-hover:opacity-100" /> : <ChevronRight size={10} className="opacity-50 group-hover:opacity-100" />}
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="space-y-1.5 pb-2">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Shell: React.FC<ShellProps> = ({ children }) => {
  const { 
    sidebarOpen,
    setSidebarOpen,
    role, 
    setRole, 
    lang, 
    setLang, 
    currentRoute, 
    setCurrentRoute, 
    alerts, 
    workQueue,
    aiCounselOpen,
    setAiCounselOpen,
    aiCounselWidth,
    setAiCounselWidth
  } = useAppContext();

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    core: true,
    people: true,
    authoring: true,
    intelligence: true,
    operations: true,
    biz: false
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const { user, firmRole, signOut } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const isMobileViewport = useMediaQuery(`(max-width: ${RESPONSIVE_BREAKPOINTS.shellMobileMax}px)`);

  useEffect(() => {
    if (!isMobileViewport) {
      setMobileNavOpen(false);
    }
  }, [isMobileViewport]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const isAr = lang === "ar";
  
  // Scopes based on role
  const canSeeOwnerCockpit = role === "OWNER" || role === "ADMIN";
  const unreadAlertsCount = alerts.filter(a => a.status === "UNRESOLVED").length;
  const activeQueueCount = workQueue.filter(w => w.status !== "COMPLETED").length;

  const handleRouteClick = (route: string) => {
    if (isMobileViewport) {
      setMobileNavOpen(false);
    }

    if (route === "/cockpit" && role === "MEMBER") {
      setCurrentRoute("/my-day");
    } else {
      setCurrentRoute(route);
    }
  };

  const mobilePrimaryRoute = canSeeOwnerCockpit ? "/cockpit" : "/my-day";
  const displayName = user?.displayName?.trim() || user?.email || "Signed in";
  const initials = (user?.displayName || user?.email || "SA")
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "SA";

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
    }
  };

  const listItems = {
    brand: isAr ? "آي-أم فولت" : "I-AM VAULT",
    subtitle: isAr ? "نظام التشغيل القانوني" : "LEGAL OS",
    secCore: isAr ? "الأساسيات" : "Core",
    cockpit: isAr ? "لوحة القيادة" : "Firm Portfolio",
    myDay: isAr ? "يومي الحالي" : "My Day",
    pipeline: isAr ? "إدارة العلاقات" : "CRM",
    calendar: isAr ? "التقويم" : "Calendar",
    alerts: isAr ? "التنبيهات" : "Alert stream",
    secPeople: isAr ? "العلاقات والملفات" : "People & Portfolio",
    clients: isAr ? "العملاء" : "Clients",
    cases: isAr ? "القضايا (النزاعات)" : "Cases Workspace",
    contracts: isAr ? "العقود والاتفاقيات" : "Contracts Workspace",
    parties: isAr ? "الأطراف الثالثة" : "Authorized Parties",
    secCreate: isAr ? "التحرير الذاتي" : "Authoring Studio",
    drafting: isAr ? "استوديو الصياغة" : "Drafting Studio",
    secIntelligence: isAr ? "الذكاء الاصطناعي" : "Legal Intelligence",
    agentWorkspace: isAr ? "مساحة عمل العميل" : "Agent Workspace",
    clauseLibrary: isAr ? "مكتبة البنود" : "Clause Library",
    templates: isAr ? "النماذج الجاهزة" : "Document Templates",
    knowledgeBase: isAr ? "المعرفة القضائية" : "Knowledge Base",
    team: isAr ? "إدارة الفريق" : "Team Management",
    secOperations: isAr ? "العمليات التنفيذية" : "Firm Operations",
    tasks: isAr ? "المهام والواجبات" : "Actionable Tasks",
    workQueue: isAr ? "طابور العمل" : "Work Queue",
    timeTracking: isAr ? "تسجيل الوقت" : "Time Timer",
    billing: isAr ? "مستندات الفواتير" : "Billing Ledger",
    secAnalyze: isAr ? "التحليلات والأرشيف" : "Business Intelligence",
    analytics: isAr ? "التحليلات والمؤشرات" : "Firm Analytics",
    search: isAr ? "البحث الشامل" : "Faceted Search",
    communications: isAr ? "الاتصالات والبريد" : "Client Comms Feed",
    documents: isAr ? "مركز المستندات" : "Document Storage",
    settings: isAr ? "إعدادات النظام" : "Firm Settings"
  };

  return (
    <div 
      className={`flex h-screen w-screen overflow-hidden bg-[#F4F4F6] select-none text-slate-800 ${
        isAr ? "flex-row-reverse" : "flex-row"
      }`}
    >
      {isMobileViewport && mobileNavOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setMobileNavOpen(false)}
          className="fixed inset-0 z-20 bg-slate-950/30 backdrop-blur-[1px] lg:hidden"
        />
      )}

      {/* 1. LEFT SIDEBAR (Redesigned with rounded corners and floating premium feel) */}
      <aside 
        className={`flex flex-col h-screen select-none z-30 shrink-0 transition-all duration-300 ease-in-out ${
          isMobileViewport
            ? `fixed top-0 bottom-0 ${isAr ? "right-0" : "left-0"} w-[86vw] max-w-[340px] p-3 ${mobileNavOpen ? "translate-x-0 opacity-100" : isAr ? "translate-x-full opacity-0 pointer-events-none" : "-translate-x-full opacity-0 pointer-events-none"}`
            : sidebarOpen ? "relative p-4 w-80 opacity-100" : "relative p-3 w-20 opacity-100"
        } ${isAr ? "font-sans font-medium" : "font-sans"}`}
      >
        <div className="bg-white/95 backdrop-blur-md rounded-2xl border border-slate-200/50 shadow-[0_12px_40px_rgba(15,23,42,0.04)] flex flex-col h-full overflow-hidden transition-all duration-300">
          {/* Upper Portion */}
          <div className="flex flex-col flex-1 overflow-y-auto no-scrollbar">
          {/* Brand Logo Card top */}
          <div className={`p-4 pb-2 flex items-center gap-3 ${!sidebarOpen ? "justify-center" : ""}`}>
            <div className="p-2.5 bg-slate-900 text-white rounded-xl shadow-sm flex items-center justify-center transition-all shrink-0">
              <Scale size={16} />
            </div>
            {sidebarOpen && (
              <div className="animate-fade-in duration-200 min-w-0">
                <h1 className="font-display text-sm font-black text-slate-950 tracking-[0.06em] leading-none uppercase truncate">
                  {listItems.brand}
                </h1>
                <span className="text-[8px] text-slate-400 font-bold tracking-widest block mt-1 uppercase truncate">
                  {listItems.subtitle}
                </span>
              </div>
            )}
          </div>

          {/* Connected Grounded Scope Indicator - Matches AI Counsel Scope display */}
          <div className={`py-2 pb-4 transition-all ${sidebarOpen ? "px-4" : "px-2"}`}>
            {sidebarOpen ? (
              <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-3 flex items-center justify-between text-[11px] text-slate-500 font-mono shadow-[inset_0_1px_2px_rgba(0,0,0,0.02)]">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="p-1 px-1.5 bg-slate-900 text-white rounded-lg shadow-sm flex items-center justify-center shrink-0">
                    <UserCheck size={11} />
                  </div>
                  <div className="truncate">
                    <span className="block text-[8px] text-slate-400 font-bold tracking-wider uppercase leading-none mb-0.5">{isAr ? "الصلاحية النشطة" : "ACTIVE PROFILE"}</span>
                    <span className="font-extrabold text-slate-800 uppercase leading-none text-[10px] truncate block">{role}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full text-[9px] font-bold border border-emerald-100/45 shrink-0 select-none">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  <span>{isAr ? "مباشر" : "LIVE"}</span>
                </div>
              </div>
            ) : (
              <div className="flex justify-center">
                <div className="relative p-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200/60 rounded-xl shadow-2xs flex items-center justify-center shrink-0" title={`${role} Role Activated`}>
                  <UserCheck size={13} className="text-slate-700" />
                  <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500 border border-white"></span>
                </div>
              </div>
            )}
          </div>

          {/* Nav list */}
          <nav className={`pb-6 flex-1 transition-all ${sidebarOpen ? "px-4" : "px-1.5"}`}>
            <NavSection title={listItems.secCore} isOpen={expandedSections.core} onToggle={() => toggleSection("core")} isAr={isAr} sidebarOpen={sidebarOpen}>
              {/* Cockpit / My Day */}
              {canSeeOwnerCockpit ? (
                <button
                  onClick={() => handleRouteClick("/cockpit")}
                  title={listItems.cockpit}
                  className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 text-[11px] ${
                    !sidebarOpen ? "justify-center p-2.5" : "text-left px-3 py-2"
                  } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                    currentRoute === "/cockpit" 
                      ? "bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                      : "text-slate-500 font-semibold hover:text-slate-950 hover:bg-slate-50"
                  }`}
                >
                  <Building2 size={15} />
                  {sidebarOpen && <span className="flex-1 truncate">{listItems.cockpit}</span>}
                </button>
              ) : (
                <button
                  onClick={() => handleRouteClick("/my-day")}
                  title={listItems.myDay}
                  className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 text-[11px] ${
                    !sidebarOpen ? "justify-center p-2.5" : "text-left px-3 py-2"
                  } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                    currentRoute === "/my-day" 
                      ? "bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                      : "text-slate-500 font-semibold hover:text-slate-950 hover:bg-slate-50"
                  }`}
                >
                  <Clock size={15} />
                  {sidebarOpen && <span className="flex-1 truncate">{listItems.myDay}</span>}
                </button>
              )}

              <button
                onClick={() => handleRouteClick("/pipeline")}
                title={listItems.pipeline}
                className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 text-[11px] ${
                  !sidebarOpen ? "justify-center p-2.5" : "text-left px-3 py-2"
                } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                  currentRoute === "/pipeline" 
                    ? "bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                    : "text-slate-500 font-semibold hover:text-slate-950 hover:bg-slate-50"
                }`}
              >
                <Layers size={15} />
                {sidebarOpen && <span className="flex-1 truncate">{listItems.pipeline}</span>}
              </button>

              <button
                onClick={() => handleRouteClick("/calendar")}
                title={listItems.calendar}
                className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 text-[11px] ${
                  !sidebarOpen ? "justify-center p-2.5" : "text-left px-3 py-2"
                } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                  currentRoute === "/calendar" 
                    ? "bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                    : "text-slate-500 font-semibold hover:text-slate-950 hover:bg-slate-50"
                }`}
              >
                <Calendar size={15} />
                {sidebarOpen && <span className="flex-1 truncate">{listItems.calendar}</span>}
              </button>

              <button
                onClick={() => handleRouteClick("/alerts")}
                title={listItems.alerts}
                className={`w-full flex items-center justify-between rounded-xl transition-all duration-150 text-[11px] ${
                  !sidebarOpen ? "justify-center p-2.5" : "px-3 py-2"
                } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                  currentRoute === "/alerts" 
                    ? "bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                    : "text-slate-500 font-semibold hover:text-slate-950 hover:bg-slate-50"
                }`}
              >
                <div className={`flex items-center gap-3 ${!sidebarOpen ? "" : "min-w-0 flex-1"} ${isAr && sidebarOpen ? "flex-row-reverse" : ""}`}>
                  <Flag size={15} />
                  {sidebarOpen && <span className="truncate">{listItems.alerts}</span>}
                </div>
                {unreadAlertsCount > 0 && sidebarOpen && (
                  <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-full font-bold leading-none shrink-0 ${
                    currentRoute === "/alerts" 
                      ? "bg-white text-slate-900 shadow-xs" 
                      : "bg-red-500 text-white shadow-sm"
                  }`}>
                    {unreadAlertsCount}
                  </span>
                )}
              </button>
            </NavSection>

            {/* PEOPLE SECTION */}
            <NavSection title={listItems.secPeople} isOpen={expandedSections.people} onToggle={() => toggleSection("people")} isAr={isAr} sidebarOpen={sidebarOpen}>
              {[
                { r: "/clients", label: listItems.clients, icon: Users },
                { r: "/cases", label: listItems.cases, icon: Scale },
                { r: "/contracts", label: listItems.contracts, icon: FileText },
                { r: "/parties", label: listItems.parties, icon: Users, sub: true },
              ].map(({ r, label, icon: Icon, sub }) => (
                <button
                  key={r}
                  onClick={() => handleRouteClick(r)}
                  title={label}
                  className={`w-full flex items-center gap-3 rounded-xl transition-all duration-205 text-[11px] ${
                    !sidebarOpen ? "justify-center p-2.5" : "text-left px-3 py-2"
                  } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                    currentRoute === r || (r.includes("cases") && currentRoute.startsWith("/cases")) || (r.includes("contracts") && currentRoute.startsWith("/contracts"))
                      ? "bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                      : "text-slate-500 font-semibold hover:text-slate-950 hover:bg-slate-50"
                  }`}
                >
                  <Icon size={15} className={sub ? "opacity-75" : ""} />
                  {sidebarOpen && <span className="flex-1 truncate">{label}</span>}
                </button>
              ))}
            </NavSection>

            {/* CREATE SECTOR */}
            <NavSection title={listItems.secCreate} isOpen={expandedSections.authoring} onToggle={() => toggleSection("authoring")} isAr={isAr} sidebarOpen={sidebarOpen}>
              <button
                onClick={() => handleRouteClick("/drafting")}
                title={listItems.drafting}
                className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 text-[11px] ${
                  !sidebarOpen ? "justify-center p-2.5" : "text-left px-3 py-2"
                } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                  currentRoute === "/drafting" 
                    ? "bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                    : "text-slate-500 font-semibold hover:text-slate-950 hover:bg-slate-50"
                }`}
              >
                <PenTool size={15} />
                {sidebarOpen && <span className="flex-1 truncate">{listItems.drafting}</span>}
              </button>
            </NavSection>

            {/* INTELLIGENCE SECTION */}
            <NavSection title={listItems.secIntelligence} isOpen={expandedSections.intelligence} onToggle={() => toggleSection("intelligence")} isAr={isAr} sidebarOpen={sidebarOpen}>
              {canSeeOwnerCockpit && (
                <button
                  onClick={() => handleRouteClick("/team")}
                  title={listItems.team}
                  className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 text-[11px] ${
                    !sidebarOpen ? "justify-center p-2.5" : "text-left px-3 py-2"
                  } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                    currentRoute === "/team" || currentRoute.startsWith("/settings")
                      ? "bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                      : "text-slate-500 font-semibold hover:text-slate-950 hover:bg-slate-50"
                  }`}
                >
                  <Users size={15} />
                  {sidebarOpen && <span className="flex-1 truncate">{listItems.team}</span>}
                </button>
              )}

              <button
                onClick={() => handleRouteClick("/agent-workspace")}
                title={listItems.agentWorkspace}
                className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 text-[11px] ${
                  !sidebarOpen ? "justify-center p-2.5" : "text-left px-3 py-2"
                } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                  currentRoute === "/agent-workspace" 
                    ? "bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                    : "text-slate-500 font-semibold hover:text-slate-950 hover:bg-slate-50"
                }`}
              >
                <Bot size={15} className={currentRoute === "/agent-workspace" ? "text-white" : "text-orange-500"} />
                {sidebarOpen && <span className="flex-1 truncate">{listItems.agentWorkspace}</span>}
              </button>

              <button
                onClick={() => handleRouteClick("/clause-library")}
                title={listItems.clauseLibrary}
                className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 text-[11px] ${
                  !sidebarOpen ? "justify-center p-2.5" : "text-left px-3 py-2"
                } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                  currentRoute === "/clause-library" 
                    ? "bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                    : "text-slate-500 font-semibold hover:text-slate-950 hover:bg-slate-50"
                }`}
              >
                <Layers size={15} />
                {sidebarOpen && <span className="flex-1 truncate">{listItems.clauseLibrary}</span>}
              </button>

              <button
                onClick={() => handleRouteClick("/templates")}
                title={listItems.templates}
                className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 text-[11px] ${
                  !sidebarOpen ? "justify-center p-2.5" : "text-left px-3 py-2"
                } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                  currentRoute === "/templates" 
                    ? "bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                    : "text-slate-500 font-semibold hover:text-slate-950 hover:bg-slate-50"
                }`}
              >
                <PlusCircle size={15} />
                {sidebarOpen && <span className="flex-1 truncate">{listItems.templates}</span>}
              </button>

              <button
                onClick={() => handleRouteClick("/knowledge-base")}
                title={listItems.knowledgeBase}
                className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 text-[11px] ${
                  !sidebarOpen ? "justify-center p-2.5" : "text-left px-3 py-2"
                } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                  currentRoute === "/knowledge-base" 
                    ? "bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                    : "text-slate-500 font-semibold hover:text-slate-950 hover:bg-slate-50"
                }`}
              >
                <BookOpen size={15} />
                {sidebarOpen && <span className="flex-1 truncate">{listItems.knowledgeBase}</span>}
              </button>
            </NavSection>

            {/* OPERATIONS */}
            <NavSection title={listItems.secOperations} isOpen={expandedSections.operations} onToggle={() => toggleSection("operations")} isAr={isAr} sidebarOpen={sidebarOpen}>
              <button
                onClick={() => handleRouteClick("/tasks")}
                title={listItems.tasks}
                className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 text-[11px] ${
                  !sidebarOpen ? "justify-center p-2.5" : "text-left px-3 py-2"
                } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                  currentRoute === "/tasks" 
                    ? "bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                    : "text-slate-500 font-semibold hover:text-slate-950 hover:bg-slate-50"
                }`}
              >
                <CheckSquare size={15} />
                {sidebarOpen && <span className="flex-1 truncate">{listItems.tasks}</span>}
              </button>

              <button
                onClick={() => handleRouteClick("/work-queue")}
                title={listItems.workQueue}
                className={`w-full flex items-center justify-between rounded-xl transition-all duration-200 text-[11px] ${
                  !sidebarOpen ? "justify-center p-2.5" : "px-3 py-2"
                } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                  currentRoute === "/work-queue" 
                    ? "bg-slate-955 bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                    : "text-slate-500 font-semibold hover:text-slate-955 hover:bg-slate-50"
                }`}
              >
                <div className={`flex items-center gap-3 ${!sidebarOpen ? "" : "min-w-0 flex-1"} ${isAr && sidebarOpen ? "flex-row-reverse" : ""}`}>
                  <Layers size={15} />
                  {sidebarOpen && <span className="truncate">{listItems.workQueue}</span>}
                </div>
                {activeQueueCount > 0 && sidebarOpen && (
                  <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-full font-bold leading-none shrink-0 ${
                    currentRoute === "/work-queue" 
                      ? "bg-white text-[#0f172a] shadow-xs" 
                      : "bg-orange-105 bg-orange-100 text-orange-600 border border-orange-200/40 shadow-sm"
                  }`}>
                    {activeQueueCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => handleRouteClick("/time-tracking")}
                title={listItems.timeTracking}
                className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 text-[11px] ${
                  !sidebarOpen ? "justify-center p-2.5" : "text-left px-3 py-2"
                } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                  currentRoute === "/time-tracking" 
                    ? "bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                    : "text-slate-500 font-semibold hover:text-slate-950 hover:bg-slate-50"
                }`}
              >
                <Clock size={15} />
                {sidebarOpen && <span className="flex-1 truncate">{listItems.timeTracking}</span>}
              </button>

              <button
                onClick={() => handleRouteClick("/billing")}
                title={listItems.billing}
                className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 text-[11px] ${
                  !sidebarOpen ? "justify-center p-2.5" : "text-left px-3 py-2"
                } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                  currentRoute === "/billing" 
                    ? "bg-slate-950 text-white font-black shadow-md shadow-slate-955 shadow-slate-950/10 scale-[1.01]" 
                    : "text-slate-500 font-semibold hover:text-slate-950 hover:bg-slate-50"
                }`}
              >
                <CreditCard size={15} />
                {sidebarOpen && <span className="flex-1 truncate">{listItems.billing}</span>}
              </button>
            </NavSection>

            {/* SYSTEM CONFIGS & ANALYTICS */}
            <NavSection title={listItems.secAnalyze} isOpen={expandedSections.biz} onToggle={() => toggleSection("biz")} isAr={isAr} sidebarOpen={sidebarOpen}>
              {[
                { r: "/analytics", label: listItems.analytics, icon: BarChart3 },
                { r: "/search", label: listItems.search, icon: Search },
                { r: "/communications", label: listItems.communications, icon: Mail },
                { r: "/documents", label: listItems.documents, icon: Files },
                { r: "/settings", label: listItems.settings, icon: Settings },
              ].map(({ r, label, icon: Icon }) => (
                <button
                  key={r}
                  onClick={() => handleRouteClick(r)}
                  title={label}
                  className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 text-[11px] ${
                    !sidebarOpen ? "justify-center p-2.5" : "text-left px-3 py-2"
                  } ${isAr && sidebarOpen ? "text-right flex-row-reverse" : ""} ${
                    currentRoute === r
                      ? "bg-slate-950 text-white font-black shadow-md shadow-slate-950/10 scale-[1.01]" 
                      : "text-slate-500 font-semibold hover:text-slate-955 hover:bg-slate-50"
                  }`}
                >
                  <Icon size={15} />
                  {sidebarOpen && <span className="flex-1 truncate">{label}</span>}
                </button>
              ))}
            </NavSection>
          </nav>
        </div>

        {/* Bottom User Area & Quick Simulated Controls (Figma left footer style) */}
        <div className={`border-t border-[#e2e8f0]/80 bg-slate-50/70 p-3 flex flex-col transition-all duration-300 ${!sidebarOpen ? "items-center gap-3" : "space-y-4"}`}>
          {sidebarOpen ? (
            <>
              {/* User profile row */}
              <div className="bg-white border border-slate-200/60 rounded-2xl p-3 shadow-3xs flex items-center gap-3">
                <div className="h-9 w-9 bg-slate-900 text-white rounded-xl font-extrabold flex items-center justify-center text-sm shadow-md shrink-0 border border-slate-800/20">
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-[11.5px] font-black text-slate-900 truncate leading-none mb-1">
                    {displayName}
                  </h4>
                  <button 
                    type="button"
                    onClick={handleSignOut}
                    disabled={signingOut}
                    className="text-[8.5px] text-slate-400 hover:text-orange-600 font-bold tracking-wider inline-flex items-center gap-1 uppercase underline text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <LogOut size={10} />
                    {signingOut ? (isAr ? "جار الخروج" : "SIGNING OUT") : (isAr ? "تسجيل الخروج" : "SIGN OUT")}
                  </button>
                </div>
                <div className="text-[9.5px] bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded-full text-slate-500 font-extrabold shrink-0 shadow-3xs">
                  {(firmRole || role).slice(0, 3)}
                </div>
              </div>

              {/* Quick Configs (Language, simulated role toggle) */}
              <div className="space-y-3 pt-1 text-[11px]">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 font-semibold font-mono text-[9px] tracking-wider uppercase">{isAr ? "اللغة:" : "Language:"}</span>
                  <div className="flex gap-1 bg-white p-0.5 rounded-xl border border-slate-200 shadow-3xs">
                    <button 
                      onClick={() => setLang("en")}
                      className={`px-3 py-1 rounded-lg text-[9.5px] font-mono font-bold transition-all ${
                        lang === "en" ? "bg-slate-900 text-white shadow-3xs" : "text-slate-400 hover:text-slate-800"
                      }`}
                    >
                      EN
                    </button>
                    <button 
                      onClick={() => setLang("ar")}
                      className={`px-3 py-1 rounded-lg text-[9.5px] font-mono font-bold transition-all ${
                        lang === "ar" ? "bg-slate-900 text-white shadow-3xs" : "text-slate-400 hover:text-slate-800"
                      }`}
                    >
                      AR
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-slate-400 font-semibold font-mono text-[9px] tracking-wider uppercase">{isAr ? "المظهر:" : "Theme:"}</span>
                  <button
                    data-testid="shell-theme-toggle"
                    type="button"
                    onClick={toggleTheme}
                    className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-1 text-[9.5px] font-mono font-bold text-slate-500 shadow-3xs transition-all hover:text-slate-900"
                    title={isDark ? "Switch to light mode" : "Switch to dark mode"}
                  >
                    <span className={`h-2 w-2 rounded-full ${isDark ? "bg-indigo-400" : "bg-orange-500"}`} />
                    {isDark ? (isAr ? "داكن" : "DARK") : (isAr ? "فاتح" : "LIGHT")}
                  </button>
                </div>

                {USE_MOCK_DATA && <div className="space-y-1.5 pt-2 border-t border-[#e2e8f0]/65">
                  <span className="text-[9px] text-slate-400 font-mono font-bold tracking-wider block uppercase">
                    {isAr ? "محاكاة الفرز الصلاحيات:" : "SIMULATED ROLE FILTER:"}
                  </span>
                  <div className="grid grid-cols-2 gap-1 text-[8.5px] font-mono">
                    {(["OWNER", "MEMBER", "ACCOUNTANT", "READ_ONLY"] as RoleType[]).map((r) => (
                      <button
                        key={r}
                        onClick={() => setRole(r)}
                        className={`py-1 px-0.5 rounded-lg transition-all text-center border font-bold ${
                          role === r 
                            ? "bg-[#0f172a] text-white border-transparent shadow-3xs" 
                            : "bg-white text-slate-400 border-slate-200 hover:border-slate-350 hover:text-slate-800 hover:shadow-3xs"
                        }`}
                      >
                        {r.replace("_", "")}
                      </button>
                    ))}
                  </div>
                </div>}
              </div>
            </>
          ) : (
            <>
              <button
                data-testid="shell-theme-toggle"
                type="button"
                onClick={toggleTheme}
                title={isDark ? "Switch to light mode" : "Switch to dark mode"}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-3xs transition-all hover:text-slate-900"
              >
                <span className={`h-2.5 w-2.5 rounded-full ${isDark ? "bg-indigo-400" : "bg-orange-500"}`} />
              </button>
              <button
                onClick={() => setSidebarOpen(true)}
                title={displayName}
                className="h-10 w-10 bg-slate-900 hover:bg-slate-850 text-white rounded-xl font-extrabold flex items-center justify-center text-xs shadow-md shrink-0 border border-slate-800/20 cursor-pointer transition-all active:scale-95"
              >
                {initials}
              </button>
            </>
          )}
        </div>
      </div>
    </aside>

      {/* 2. MAIN HUB CONTENT AND WRAPPERS */}
      <div className={`flex-1 flex h-screen overflow-hidden pb-20 lg:pb-0 ${isAr ? "flex-row-reverse" : "flex-row"}`}>
        {children}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/95 px-2 py-2 backdrop-blur lg:hidden">
        <div className="grid grid-cols-5 gap-2">
          {[
            { key: "menu", label: "Menu", icon: Layers, action: () => { setSidebarOpen(true); setMobileNavOpen(true); }, active: mobileNavOpen, badge: unreadAlertsCount > 0 ? unreadAlertsCount : null },
            { key: mobilePrimaryRoute, label: canSeeOwnerCockpit ? "Cockpit" : "My Day", icon: canSeeOwnerCockpit ? Building2 : Calendar, action: () => handleRouteClick(mobilePrimaryRoute), active: currentRoute === mobilePrimaryRoute },
            { key: "/search", label: "Search", icon: Search, action: () => handleRouteClick("/search"), active: currentRoute === "/search" },
            { key: "/drafting", label: "Drafting", icon: PenTool, action: () => handleRouteClick("/drafting"), active: currentRoute === "/drafting" },
            { key: "ai", label: "AI", icon: Bot, action: () => setAiCounselOpen(!aiCounselOpen), active: aiCounselOpen },
          ].map(({ key, label, icon: Icon, action, active, badge }) => (
            <button key={key} type="button" onClick={action} className={`relative rounded-2xl px-2 py-2.5 text-center transition ${active ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-600"}`}>
              <Icon size={16} className={`mx-auto ${active ? "text-orange-300" : "text-orange-600"}`} />
              <span className="mt-1 block text-[10px] font-black tracking-tight">{label}</span>
              {badge ? <span className={`absolute right-2 top-1 rounded-full px-1.5 py-0.5 text-[9px] font-mono font-bold ${active ? "bg-white text-slate-950" : "bg-orange-500 text-white"}`}>{badge}</span> : null}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
